import React from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import classNames from 'classnames';
import { connect } from 'react-redux';
import Heading from '@cotyorg/ccx-atoms/Heading';
import IconButton from '@cotyorg/ccx-molecules/IconButton';
import ShowHide from '@cotyorg/ccx-utils/js/components/ShowHide';
import { CardsCarousel } from '@cotyorg/ccx-organism-carousel';
import Breadcrumbs from '@cotyorg/ccx-molecules/Breadcrumbs';
import { ProductGalleryThumbnails } from '@cotyorg/ccx-organism-product-gallery';
import { trackCarouselClickEvent } from '@cotyorg/ccx-utils/js/tracking/adobe-dtm/events/carousel';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import StandardLayout from '../../templates/StandardLayout';
import ProductInfo from '../../organisms/ProductInfo';
import { isSmallBreakpoint } from '../../utils';
import { toggle as toggleAction } from './modules/actions';
import { schema } from './helpers';
import ShadeFinder from '../../organisms/ShadeFinder/components/ShadeFinder';

const cardsPerPageMap = {
  xsmall: 1,
  small: 2,
  medium: 3,
  large: 4,
  xlarge: 4,
};

const card = {
  cardsStoreDataKey: 'relatedProducts',
  isTitleUnderlined: false,
  hasBackground: false,
  isSmallTitle: true,
  fullWidth: false,
  headingLevel: 5,
  fixedHeight: true,
  zoomOnHover: true,
  hideDescription: true,
  showStarRating: true,
  imgRatingOnSrc: {
    fullStar: '/ccx-files/assets/star-on.svg',
    partialStar1: '/ccx-files/assets/partial-star-1.svg',
    partialStar2: '/ccx-files/assets/partial-star-2.svg',
    partialStar3: '/ccx-files/assets/partial-star-3.svg',
    partialStar4: '/ccx-files/assets/partial-star-4.svg',
    partialStar5: '/ccx-files/assets/partial-star-5.svg',
    partialStar6: '/ccx-files/assets/partial-star-6.svg',
    partialStar7: '/ccx-files/assets/partial-star-7.svg',
    partialStar8: '/ccx-files/assets/partial-star-8.svg',
    partialStar9: '/ccx-files/assets/partial-star-9.svg',
  },
  imgRatingOffSrc: '/ccx-files/assets/star-off.svg',
  ratingIconWidth: 20,
  ratingIconHeight: 20,
};

const carousel = {
  carouselsStoreDataKey: 'relatedProductsCarousel',
  headingLevel: 5,
  rightChevronIconSrc: '/ccx-files/assets/arrow-right.svg',
  leftChevronIconSrc: '/ccx-files/assets/arrow-left.svg',
  onIndexChange: ({ type, index, isForward }) =>
    trackCarouselClickEvent({
      page: 'product',
      carousel: 'relatedProductsCarousel',
      type,
      index,
      isForward,
    }),
};

const generateContainerClassNames = (isSmall) =>
  classNames('grid-container', {
    full: isSmall,
  });

const Product = ({
  productDetails,
  sectionHeadings,
  currentBreakpoint,
  selectedSwatch,
  breadcrumb,
  isIngredientsOpen = false,
  toggle,
  subcategoryTitle,
  categoryTitle,
  productSwatch,
  retailers,
  currentUrl,
  tracking,
  shouldUseBazaarVoice,
  shouldShowRetailers,
  locale,
  canonicalUrl,
  shadeFinder,
  enableShadeFinder,
  productSlug,
  hidePrice,
  disclaimer,
}) => {
  const isShadeFinderEnabled = Boolean(enableShadeFinder && shadeFinder);

  return (
    <StandardLayout>
      <div className="product-page">
        <Breadcrumbs items={breadcrumb} />

        <div className="pre-offset-vertical">
          <div
            className={generateContainerClassNames(
              isSmallBreakpoint({ currentBreakpoint })
            )}
          >
            <div className="product-page__product">
              <div className="grid-x">
                {isSmallBreakpoint({ currentBreakpoint }) && (
                  <div className="cell xsmall-16">
                    <Heading
                      extraClass="h2 product-page__heading"
                      level={1}
                      itemProp="name"
                    >
                      {R.path(['productDetails', 'heading'], sectionHeadings)}
                    </Heading>
                  </div>
                )}

                <div className="cell xsmall-16 medium-offset-1 medium-7 large-7 xlarge-8">
                  <div className="product-page__product-gallery">
                    <ProductGalleryThumbnails
                      headingLevel={3}
                      rightChevronIconSrc="/ccx-files/assets/arrow-right.svg"
                      leftChevronIconSrc="/ccx-files/assets/arrow-left.svg"
                      carouselsStoreDataKey="productGallery"
                      onIndexChange={({ type, index, isForward }) =>
                        trackCarouselClickEvent({
                          page: 'product',
                          carousel: 'productGallery',
                          type,
                          index,
                          isForward,
                        })
                      }
                    />
                  </div>
                  {isShadeFinderEnabled && (
                    <div className="product-page__section-try-on">
                      <ShadeFinder
                        shadeFinderConfiguration={shadeFinder}
                        vtoActualProductSlug={productSlug}
                        actualProductSku={R.prop('sku', selectedSwatch)}
                      />
                    </div>
                  )}
                </div>

                <div className="cell xsmall-16 medium-8 large-8 xlarge-7">
                  <ProductInfo
                    sectionHeadings={sectionHeadings}
                    productDetails={productDetails}
                    currentBreakpoint={currentBreakpoint}
                    subcategoryTitle={subcategoryTitle}
                    categoryTitle={categoryTitle}
                    productSwatch={productSwatch}
                    useBazaarvoice={shouldUseBazaarVoice}
                    shouldShowRetailers={shouldShowRetailers}
                    locale={locale}
                    hidePrice={hidePrice}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="show-ingredients">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="cell xsmall-16 medium-16 xlarge-10 xlarge-offset-3">
                <div className="grid-x align-center">
                  {isIngredientsOpen ? (
                    <IconButton
                      iconWidth={48}
                      testAttribute="show-ingredients-button"
                      iconSrc="/ccx-files/assets/circle-minus.svg"
                      size="medium"
                      extraClass="show-ingredients__show-more-button"
                      onClick={toggle}
                    >
                      {R.prop('hideIngredients', productDetails)}
                    </IconButton>
                  ) : (
                    <IconButton
                      iconWidth={48}
                      testAttribute="show-ingredients-button"
                      iconSrc="/ccx-files/assets/circle-plus.svg"
                      size="medium"
                      extraClass="show-ingredients__show-more-button"
                      onClick={toggle}
                    >
                      {R.prop('showIngredients', productDetails)}
                    </IconButton>
                  )}
                </div>

                <ShowHide
                  isOpen={isIngredientsOpen}
                  extraClass="show-ingredients__show-hide"
                  noTransitionClass="show-ingredients__show-hide--transition"
                >
                  <div
                    data-test="accordion-item-title-button"
                    className="show-ingredients__show-hide__text"
                  >
                    {disclaimer && <p className="disclaimer">{disclaimer}</p>}
                    {R.prop('ingredients', selectedSwatch)}
                  </div>
                </ShowHide>
              </div>
            </div>
          </div>
        </div>

        <div className="related-products">
          <div className="grid-container">
            <div className="grid-x grid-padding-x">
              <div className="cell xsmall-16">
                <Heading level={2} extraClass="related-products__heading">
                  {R.path(['relatedProducts', 'heading'], sectionHeadings)}
                </Heading>

                <CardsCarousel
                  cardsPerPageMap={cardsPerPageMap}
                  cards={card}
                  carousel={carousel}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <OptionalSection
        renderCondition={shouldUseBazaarVoice}
        section={() => (
          <div className="product-reviews" id="reviews">
            <div className="grid-container">
              <div className="product-reviews__box">
                <Heading
                  data-test="product-reviews-heading"
                  level={2}
                  extraClass="product-reviews__heading"
                >
                  {R.path(['reviews', 'heading'], sectionHeadings)}
                </Heading>

                <div
                  data-bv-show="inline_rating"
                  data-bv-product-id={R.prop('bvid', productDetails)}
                  data-bv-seo={false}
                />

                <div
                  data-bv-show="reviews"
                  data-bv-productId={R.prop('bvid', productDetails)}
                />
              </div>
            </div>
          </div>
        )}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            schema({
              sectionHeadings,
              productDetails,
              retailers,
              currentUrl,
              canonicalUrl,
              tracking,
            })
          ),
        }}
      />
    </StandardLayout>
  );
};

const mapStateToProps = ({
  content,
  breakpoints,
  productSwatch,
  product,
  status,
}) => {
  const selectedSKU = R.prop('selectedSKU')(productSwatch);

  const swatches = R.path(['productSwatch', 'swatches'])(content);
  const selectedSwatch = R.find(({ sku }) => sku === selectedSKU)(swatches);

  return {
    sectionHeadings: R.prop('sectionHeadings')(content),
    currentBreakpoint: R.prop('current')(breakpoints),
    productDetails: R.prop('productDetails')(content),
    selectedSwatch,
    breadcrumb: R.prop('breadcrumb')(content),
    isIngredientsOpen: R.prop('isIngredientsOpen')(product),
    subcategoryTitle: R.prop('subcategoryTitle')(content),
    categoryTitle: R.prop('categoryTitle')(content),
    productSwatch: R.prop('productSwatch')(content),
    retailers: R.prop('retailers', content),
    currentUrl: R.prop('currentUrl', status),
    canonicalUrl: R.prop('canonicalUrl', status),
    tracking: R.prop('tracking')(content),
    shouldUseBazaarVoice: R.prop('shouldUseBazaarVoice')(content),
    shouldShowRetailers: R.prop('shouldShowRetailers')(content),
    locale: R.prop('locale')(content),
    shadeFinder: R.prop('shadeFinder', content),
    enableShadeFinder: R.prop('enableShadeFinder', content),
    shadeFinderQuiz: R.prop('shadeFinderQuiz', content),
    productSlug: R.prop('productURL', content),
    hidePrice: R.prop('hidePrice', content),
    disclaimer: R.prop('disclaimer', content),
  };
};

const mapDispatchToProps = (dispatch) => ({
  toggle: () => dispatch(toggleAction()),
});

Product.propTypes = {
  productDetails: PropTypes.shape({
    averageRating: PropTypes.any,
    benefits: PropTypes.arrayOf(PropTypes.any),
    buyButtonLabel: PropTypes.string,
    bvid: PropTypes.string,
    description: PropTypes.string,
    howToSteps: PropTypes.arrayOf(PropTypes.any),
    showIngredients: PropTypes.string,
    hideIngredients: PropTypes.string,
    longDescription: PropTypes.string,
    outOfStockLabel: PropTypes.string,
    priceRetailersPluralLabel: PropTypes.string,
    priceRetailersSingularLabel: PropTypes.string,
    retailersNotFoundLabel: PropTypes.string,
    shadeSelectLabel: PropTypes.string,
    title: PropTypes.string,
    noReviewsLabel: PropTypes.string.isRequired,
    readReviewsLabel: PropTypes.string.isRequired,
  }),
  retailers: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      price: PropTypes.string,
      currency: PropTypes.string,
    })
  ),
  sectionHeadings: PropTypes.shape({
    relatedProducts: PropTypes.shape({
      heading: PropTypes.string,
    }),
    howToSteps: PropTypes.shape({
      heading: PropTypes.string,
    }),
    retailers: PropTypes.shape({
      heading: PropTypes.string,
    }),
  }),
  currentBreakpoint: PropTypes.string,
  selectedSwatch: PropTypes.shape({
    ingredients: PropTypes.string,
    itemName: PropTypes.string,
    sku: PropTypes.string,
    skuColor: PropTypes.string,
  }),
  breadcrumb: PropTypes.arrayOf(PropTypes.object),
  isIngredientsOpen: PropTypes.bool,
  toggle: PropTypes.func,
  subcategoryTitle: PropTypes.string,
  categoryTitle: PropTypes.string,
  productSwatch: PropTypes.shape({
    swatches: PropTypes.arrayOf(
      PropTypes.shape({
        sku: PropTypes.string,
        itemName: PropTypes.string,
        skuColor: PropTypes.string,
      })
    ),
  }),
  currentUrl: PropTypes.string.isRequired,
  canonicalUrl: PropTypes.string.isRequired,
  tracking: PropTypes.shape({
    currencyCode: PropTypes.string,
    product: PropTypes.arrayOf(
      PropTypes.shape({
        attributes: PropTypes.shape({
          productUrl: PropTypes.string,
        }),
      })
    ),
  }),
  shouldUseBazaarVoice: PropTypes.bool,
  shouldShowRetailers: PropTypes.bool,
  locale: PropTypes.string,
  shadeFinder: PropTypes.object,
  enableShadeFinder: PropTypes.bool,
  productSlug: PropTypes.string,
  hidePrice: PropTypes.bool,
  disclaimer: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(Product);
